import { useCallback, useContext, useEffect, useRef, useState } from "react";
import CryptoJS from "crypto-js";
import { toast } from "react-toastify";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import { ClipLoader } from "react-spinners";

import axiosInstance from "../../Context/axiosInstance";
import { DarkModeContext } from "../../Context/DarkModeContext";
import { GeoContext } from "../../Context/GeoContext";
import WalletCardVisaLike from "../../Components/WalletCardVisaLike";
import logo from "../../assets/darkMode_logo.png";

const WalletPage = () => {
  const { isDarkMode } = useContext(DarkModeContext);
  const userId = localStorage.getItem("userId");
  const { store } = useContext(GeoContext);
  const currency = store?.currency;

  const [balance, setBalance] = useState(0);
  const [redeemCode, setRedeemCode] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(7);
  const [totalPages, setTotalPages] = useState(1);
  const [loadingRedeem, setLoadingReedem] = useState(false);
  const [loading, setLoading] = useState(false);
  const [glow, setGlow] = useState(false);

  // new state / refs
  const [displayBalance, setDisplayBalance] = useState(0); // animated display value
  const animationRef = useRef(null); // holds rAF id
  const prevBalanceRef = useRef(0); // previous settled balance
  const DURATION_MS = 1200; // animation duration — change to taste
  const [userInfo, setUserInfo] = useState(null);

  const decryptData = (encryptedData) => {
    try {
      const bytes = CryptoJS.AES.decrypt(
        encryptedData,
        process.env.REACT_APP_SECRET_KEY,
      );
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (e) {
      return "";
    }
  };

  const decryptedId = decryptData(userId);

  // Fetch user once
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axiosInstance.get(`/users/${decryptedId}`);
        setUserInfo(res.data);
      } catch (err) {
        toast.error(err.message);
      }
    };
    fetchUser();
  }, [decryptedId]);

  // Fetch wallet info with pagination
  const fetchWallet = useCallback(
    async (pageNum = 1) => {
      if (!decryptedId) return;
      setLoading(true);
      try {
        const response = await axiosInstance.get(
          `/users/${decryptedId}/wallet?page=${pageNum}&pageSize=${pageSize}`,
        );
        setBalance(response.data.balance);
        setTransactions(response.data.transactions.response || []);
        setTotalPages(
          Math.max(
            1,
            Math.ceil((response.data.transactions.totalCount || 0) / pageSize),
          ),
        );
      } catch (error) {
        toast.error("Failed to load wallet information.");
      } finally {
        setLoading(false);
      }
    },
    [decryptedId, pageSize],
  );

  useEffect(() => {
    fetchWallet(page);
  }, [page, fetchWallet]);

  // Handle redeem
  const handleRedeem = useCallback(
    async (code = redeemCode) => {
      const trimmedCode = code.trim();

      setLoadingReedem(true);
      try {
        await axiosInstance.post(`/codes/redeem`, {
          codeValue: trimmedCode,
        });

        setRedeemCode("");
        setGlow(true);

        await fetchWallet();

        toast.success(`Code redeemed successfully!`);

        setTimeout(() => setGlow(false), 2000);
      } catch (error) {
        toast.error(error.response?.data?.detail);
        setRedeemCode("");
      } finally {
        setLoadingReedem(false);
      }
    },
    [redeemCode, fetchWallet],
  );

  // Auto redeem when full code entered
  useEffect(() => {
    if (redeemCode.length === 15) handleRedeem();
  }, [redeemCode, handleRedeem]);

  useEffect(() => {
    // nothing to animate while loading
    if (loading) return;

    const startValue = prevBalanceRef.current ?? 0;
    const targetValue = Number(balance) || 0;
    const startTime = performance.now();

    // cancel previous
    if (animationRef.current) cancelAnimationFrame(animationRef.current);

    const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

    const step = (now) => {
      const elapsed = now - startTime;
      const t = Math.min(1, elapsed / DURATION_MS);
      const eased = easeOutQuad(t);
      const current = startValue + (targetValue - startValue) * eased;
      setDisplayBalance(current);

      if (t < 1) {
        animationRef.current = requestAnimationFrame(step);
      } else {
        // done
        prevBalanceRef.current = targetValue;
        animationRef.current = null;
        setDisplayBalance(targetValue); // ensure exact final number
      }
    };

    animationRef.current = requestAnimationFrame(step);

    // cleanup if component unmounts or balance changes
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = null;
      }
    };
  }, [balance, loading]); // re-run whenever actual balance or loading changes

  return (
    <div
      className={`min-h-screen flex items-center justify-center px-4 pb-8 pt-24 sm:px-6 transition-all duration-500 ${
        isDarkMode ? "bg-[#181818] text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      {/* container limits total width and puts two equal columns side-by-side */}
      {userInfo && (
        <div className="animate-fadeIn w-full max-w-5xl grid grid-cols-1 xl:grid-cols-2 gap-6 items-center">
          {/* Left card - Wallet */}
          <WalletCardVisaLike
            isDarkMode={isDarkMode}
            displayBalance={displayBalance}
            currency={currency}
            loading={loadingRedeem}
            redeemCode={redeemCode}
            setRedeemCode={setRedeemCode}
            handleRedeem={handleRedeem}
            glow={glow}
            schoolLogo={logo}
            cardHolderInfo={userInfo}
            className="w-full min-w-0"
          />

          {/* Right card - Receipt-style Transactions */}
          <div
            className={`w-full min-w-0 rounded-2xl shadow-lg border transition-all duration-500 overflow-hidden flex flex-col font-mono text-sm ${
              isDarkMode
                ? "border-gray-700 bg-[#1a1a1a]"
                : "border-gray-300 bg-white"
            }`}
            style={{ height: "min(530px, 80dvh)", minHeight: "360px" }}
          >
            <div className="p-6 flex flex-col h-full">
              <h3
                className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                  isDarkMode ? "text-red-400" : "text-blue-500"
                }`}
              >
                <FaMoneyBillTransfer /> Transactions
              </h3>

              <div className="min-h-0 flex-1 overflow-y-auto pr-2 custom-scrollbar">
                {loading ? (
                  <div className="flex h-full items-center justify-center">
                    <ClipLoader
                      color={isDarkMode ? "#ef4444" : "#3b82f6"}
                      size={34}
                    />
                  </div>
                ) : transactions.length === 0 ? (
                  <p className="opacity-75">No transactions yet.</p>
                ) : (
                  transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="grid grid-cols-[minmax(0,1fr)_130px] items-start gap-4 mb-2 border-b border-dotted pb-2"
                    >
                      <div className="min-w-0 overflow-hidden">
                        <p
                          className="truncate text-sm font-medium"
                          title={tx.description || "Transaction"}
                        >
                          {tx.description || "Transaction"}
                        </p>
                        <p className="truncate text-xs opacity-75">
                          {new Date(tx.createdAt).toLocaleString()}
                        </p>
                      </div>
                      <div
                        className={`w-full break-words text-right font-semibold tabular-nums ${
                          tx.amount > 0 ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {tx.amount > 0 ? "+" : ""}
                        {tx.amount} {currency}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {transactions.length > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <button
                    disabled={page === 1 || loading}
                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                    className={`px-3 py-1 rounded-md text-sm ${
                      isDarkMode
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    } text-white disabled:opacity-50`}
                  >
                    Previous
                  </button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                    className={`px-3 py-1 rounded-md text-sm ${
                      isDarkMode
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-blue-600 hover:bg-blue-700"
                    } text-white disabled:opacity-50`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletPage;
